<script setup lang="ts">
const ComponentsTab = defineAsyncComponent(() => import('./ui/ComponentsTab.vue'));
const WidgetsTab = defineAsyncComponent(() => import('./ui/WidgetsTab.vue'));

type EntryKey = 'components' | 'widgets';

type Entry = {
  key: EntryKey;
  title: string;
  description: string;
  render?: () => ReturnType<typeof h>;
};

type MenuGroup = {
  key: string;
  title: string;
  entries: Entry[];
};

const menuGroups: MenuGroup[] = [
  {
    key: 'components',
    title: '组件库',
    entries: [
      {
        key: 'components',
        title: '基础组件',
        description: '展示 app/platform/design/components 目录下的所有组件',
        render: () => h(ComponentsTab),
      },
    ],
  },
  {
    key: 'widgets',
    title: '业务组件',
    entries: [
      {
        key: 'widgets',
        title: '业务组件',
        description: '展示 app/platform/design/widgets 目录下的所有组件',
        render: () => h(WidgetsTab),
      },
    ],
  },
];

const defaultEntryKey = menuGroups[0]?.entries[0]?.key ?? 'components';
const activeEntry = ref<EntryKey>(defaultEntryKey);
const mobileMenuOpen = ref(false);

const flatEntries = menuGroups.flatMap(group => group.entries);

const currentEntry = computed(() => flatEntries.find(item => item.key === activeEntry.value));

const hasRenderer = computed(() => Boolean(currentEntry.value?.render));

const handleEntryClick = (key: EntryKey) => {
  activeEntry.value = key;
  mobileMenuOpen.value = false;
};

defineExpose({ usageSections: [] });
</script>

<template>
  <div class="design-page">
    <!-- Mobile Header -->
    <header
      class="design-page__mobile-header"
      :class="{ 'design-page__mobile-header--hidden': mobileMenuOpen }"
    >
      <div class="design-page__mobile-brand">Platform 设计</div>
      <button class="design-page__mobile-menu-btn" @click="mobileMenuOpen = !mobileMenuOpen">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            v-if="!mobileMenuOpen"
            d="M2 5h16M2 10h16M2 15h16"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
          <path
            v-else
            d="M5 5l10 10M15 5l-10 10"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </header>

    <!-- Mobile Menu Overlay -->
    <div
      v-if="mobileMenuOpen"
      class="design-page__mobile-overlay"
      @click="mobileMenuOpen = false"
    ></div>

    <!-- Sidebar -->
    <aside class="design-page__sidebar" :class="{ 'design-page__sidebar--open': mobileMenuOpen }">
      <div class="design-page__brand">
        <div class="design-page__brand-logo">Platform</div>
        <div class="design-page__brand-desc">设计组件</div>
      </div>
      <nav class="design-page__nav">
        <div v-for="group in menuGroups" :key="group.key" class="design-page__nav-group">
          <div class="design-page__nav-group-title">{{ group.title }}</div>
          <button
            v-for="entry in group.entries"
            :key="entry.key"
            type="button"
            class="design-page__nav-item"
            :class="{
              'design-page__nav-item--active': activeEntry === entry.key,
            }"
            @click="handleEntryClick(entry.key)"
          >
            {{ entry.title }}
          </button>
        </div>
      </nav>
    </aside>

    <!-- Main Content -->
    <main class="design-page__main">
      <section v-if="hasRenderer" class="design-page__panel">
        <component :is="currentEntry?.render" />
      </section>

      <section v-else class="design-page__placeholder">
        <div class="design-page__placeholder-illustration"></div>
        <h3>内容整理中</h3>
        <p>该板块即将上线，敬请期待。</p>
      </section>
    </main>
  </div>
</template>

<style scoped lang="scss">
.design-page {
  display: flex;
  min-height: 100vh;
  height: 100vh;
  overflow: hidden;
  background: var(--x-back-2);
  position: relative;

  table {
    max-width: 1200px !important;
  }

  // Mobile Header
  &__mobile-header {
    display: none;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: var(--x-back-1);
    border-bottom: 1px solid var(--x-back-4);
    position: sticky;
    top: 0;
    z-index: 101;
    transition: transform 0.3s ease;

    &--hidden {
      transform: translateY(-100%);
    }
  }

  &__mobile-brand {
    font-size: 18px;
    font-weight: 700;
    color: var(--x-purple-6);
  }

  &__mobile-menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    background: transparent;
    color: var(--x-text-2);
    cursor: pointer;
    border-radius: 6px;
    transition: background-color 0.2s ease;

    &:hover {
      background: var(--x-back-3);
    }
  }

  &__mobile-overlay {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 99;
  }

  // Sidebar
  &__sidebar {
    flex: none;
    width: 240px;
    padding: 24px 16px;
    background: var(--x-back-1);
    border-right: 1px solid var(--x-back-4);
    display: flex;
    flex-direction: column;
    gap: 24px;
    position: sticky;
    top: 0;
    height: 100vh;
    align-self: flex-start;
    overflow-y: auto;
    transition: transform 0.3s ease;
  }

  &__brand {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__brand-logo {
    font-size: 20px;
    font-weight: 700;
    color: var(--x-purple-6);
  }

  &__brand-desc {
    font-size: 12px;
    color: var(--x-text-5);
  }

  &__nav {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__nav-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__nav-group-title {
    font-size: 11px;
    font-weight: 600;
    color: var(--x-text-5);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0 8px;
  }

  &__nav-item {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    height: 32px;
    padding: 0 10px;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: var(--x-text-3);
    cursor: pointer;
    font-size: 13px;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;

    &:hover {
      background: var(--x-back-3);
      color: var(--x-purple-6);
    }

    &--active {
      background: var(--x-purple-6);
      color: var(--x-back-1);
      font-weight: 600;
    }
  }

  // Main Content
  &__main {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 32px 40px;
    gap: 24px;
    background-color: var(--x-back-1);
    overflow-y: auto;
    max-height: 100vh;
  }

  &__panel {
    flex: 1;
  }

  &__placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: var(--x-back-1);
    border-radius: 12px;
    padding: 48px 24px;
    gap: 12px;
    min-height: 400px;

    h3 {
      margin: 0;
      color: var(--x-text-1);
      font-weight: 600;
      font-size: 16px;
    }

    p {
      margin: 0;
      color: var(--x-text-5);
      font-size: 14px;
    }
  }

  &__placeholder-illustration {
    width: 120px;
    height: 90px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(131, 96, 255, 0.12), rgba(64, 44, 172, 0.08));
  }

  // Mobile Styles
  @media (max-width: 680px) {
    flex-direction: column;
    height: auto;
    min-height: 100vh;

    &__mobile-header {
      display: flex;
    }

    &__mobile-overlay {
      display: block;
    }

    &__sidebar {
      position: fixed;
      top: 0;
      left: 0;
      bottom: 0;
      width: 280px;
      z-index: 100;
      transform: translateX(-100%);
      box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);

      &--open {
        transform: translateX(0);
      }
    }

    &__main {
      padding: 16px;
      gap: 16px;
      max-height: none;
    }

    &__placeholder {
      padding: 32px 16px;
      min-height: 300px;

      h3 {
        font-size: 15px;
      }

      p {
        font-size: 13px;
      }
    }

    &__placeholder-illustration {
      width: 100px;
      height: 75px;
    }
  }

  // Tablet Styles
  @media (min-width: 681px) and (max-width: 1024px) {
    &__sidebar {
      width: 200px;
      padding: 20px 12px;
    }

    &__main {
      padding: 24px 32px;
    }
  }
}
</style>
